import { initFincode, type FincodeUI } from '@fincode/js';

import { useRuntimeConfigKey, type UseRuntimeConfig } from '@/injectionKeys';
import { captureException } from '@/wabunka-src/common/utils/sentry';
import {
  buildDefaultFincodeModel,
  buildPaymentCardToken,
  convertToEnErrorMessages,
  createDefaultFincodeCardAppearance,
  validateFincodeCard,
  type PaymentCardTokenResponse,
} from '@/wabunka-src/features/Experience';

/**
 * 決済フォームは画面に 1 つだけ mount されるため、composable インスタンスごとに持たずモジュールスコープで共有する
 */
let ui: FincodeUI | undefined;
// eslint-disable-next-line @typescript-eslint/no-explicit-any
let fincodeInstance: any | undefined;

const clearFincodeInstance = () => {
  ui = undefined;
  fincodeInstance = undefined;
};

// fincode のフォームは mount 幅に比例して文字と入力欄が大きくなる（320px で 12px 弱）。PC 幅の値は CreditCardFields の lg:max-w-[500px] と対にしている
const resolveFormWidth = () => (window.matchMedia?.('(min-width: 992px)').matches ? '440' : '320');

export const useFincode = () => {
  const useRuntimeConfig = inject(useRuntimeConfigKey) as UseRuntimeConfig;
  const runtimeConfig = useRuntimeConfig();
  const publicKey = runtimeConfig.public.FINCODE_PUBLIC_KEY;

  const isProduction = runtimeConfig.public.env === 'production' || runtimeConfig.public.env === 'staging';

  const loadFincodePaymentForm = async () => {
    // 読み込み直しのたびに作り直す。失敗時に前回の ui が残ると、破棄済みフォームの値でトークン化してしまう
    clearFincodeInstance();
    try {
      fincodeInstance = await initFincode({ publicKey, isLiveMode: isProduction });
      const appearance = createDefaultFincodeCardAppearance();

      ui = fincodeInstance.ui(appearance);
      if (ui) {
        ui.create('cards', appearance);
        ui.mount('fincode', resolveFormWidth());
      }
    } catch (error) {
      clearFincodeInstance();
      console.error('Fincode initialization error:', error);
      // 本composableをtemplateタグ内で使用時、そこでリトライを行う場合は、2回目以降である旨のエラーを送信する
      // そのため本エラーメッセージは1回目のエラーである旨を明示している
      const err = new Error(`Payment form initialization failed (1st attempt): ${(error as Error).message || 'Unknown error'}`);
      captureException(err); // Sentryにもエラーを送信
      throw new Error('Failed to initialize payment form');
    }
  };

  const createFincodeToken = async (): Promise<PaymentCardTokenResponse> => {
    if (!ui) {
      return {
        error: {
          messages: ['Fincode UI has not been initialized'],
        },
      };
    }

    try {
      const result = await ui.getFormData();
      // fincode apiでチェックしない事項のバリデーション
      const validationErrors = validateFincodeCard(result);

      if (validationErrors.length) {
        return {
          error: {
            messages: validationErrors,
          },
        };
      }

      const cardData = buildDefaultFincodeModel(result);

      return new Promise<PaymentCardTokenResponse>((resolve) => {
        fincodeInstance.tokens(
          cardData,
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          (status: number, response: any) => {
            if (response.errors) {
              // eslint-disable-next-line @typescript-eslint/no-explicit-any
              response.errors.forEach((error: any) => {
                validationErrors.push(convertToEnErrorMessages(error.error_code));
              });
            }
            if (status === 200 && response.list[0].token) {
              resolve({
                data: buildPaymentCardToken(response.list[0].token, result.cardNo?.slice(-4) || ''),
              });
            } else {
              resolve({
                error: {
                  messages: validationErrors,
                },
              });
            }
          },
          () => {
            captureException(new Error('Failed to create Fincode token'));
            resolve({
              error: {
                messages: ['Unexpected error occurred.'],
              },
            });
          },
        );
      });
    } catch (error) {
      captureException(error);
      return {
        error: {
          messages: ['Unknown error occurred'],
        },
      };
    }
  };

  /**
   * 最低決済可能金額を下回っているかどうか？
   */
  const isBelowFincodeMinimumPaymentPrice = (paymentPrice: number) => {
    return paymentPrice < 1;
  };

  return {
    loadFincodePaymentForm,
    unLoadFincodePaymentForm: clearFincodeInstance,
    createFincodeToken,
    isBelowFincodeMinimumPaymentPrice,
  };
};
